import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';

const html = document.documentElement;

/**
 * The orange frame around the page.
 *
 * States live as attributes on <html> so CSS can react without JavaScript
 * having to touch layout:
 *   data-frame-open  the menu view: the panel is scaled away to nothing,
 *                    leaving the home picture and logo (with the menus on
 *                    desktop)
 *   data-menu-open   the mobile menu sheet is down over the page (the page
 *                    behind it is pinned; see the mobile rules in base.css)
 *   data-navigating  a nav or utility link has been followed and its page
 *                    is loading: the current page's dot is already gone
 *   data-home        the home page (set server-side): the panel is empty
 *                    and hidden, so the frame's home picture and logo are
 *                    the page (at every width); the frame, the logo and the
 *                    hover preview do nothing
 *
 * @extends {Component}
 */
class FrameComponent extends Component {
  /**
   * Set to 'zoom' before leaving through a menu link from the menu view, so
   * the next page is painted small and grows to full size (read by the
   * head script in theme.liquid)
   */
  static OPEN_ON_LOAD_KEY = 'frame-open-on-load';

  /** @type {number | undefined} */
  #settleTimer;

  #desktop = window.matchMedia('(min-width: 990px)');

  /** Page scroll position when the mobile menu opened, restored on close */
  #menuScrollY = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('keydown', this.#handleKeydown);
    this.addEventListener('transitionend', this.#handleTransitionEnd);
    this.addEventListener('click', this.#handleMenuLinkClick);
    window.addEventListener('pageshow', this.#handlePageShow);
    this.#desktop.addEventListener('change', this.#handleBreakpoint);

    // Arriving from the menu view: the page was painted at the small size
    // (the head script set data-frame-open from the flag) and now grows to
    // full size, the content fading in meanwhile (base.css)
    if (html.hasAttribute('data-frame-zoom-in')) {
      requestAnimationFrame(() => {
        const panel = this.querySelector('.frame__panel');
        // The small size has to be the computed starting point for the transition to run
        if (panel) void getComputedStyle(panel).transform;
        this.close();
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.#handleKeydown);
    this.removeEventListener('transitionend', this.#handleTransitionEnd);
    this.removeEventListener('click', this.#handleMenuLinkClick);
    window.removeEventListener('pageshow', this.#handlePageShow);
    this.#desktop.removeEventListener('change', this.#handleBreakpoint);
    clearTimeout(this.#settleTimer);
    this.closeMenu();
  }

  get isOpen() {
    return html.hasAttribute('data-frame-open');
  }

  get #isHome() {
    return html.hasAttribute('data-home');
  }

  /**
   * Click on the orange gutter: toggle the zoomed-out menu state instead of
   * following the link home. Without JavaScript the link still works. On the
   * home page (which never zooms) the gutter does nothing,
   * and on phones it does nothing at all.
   * @param {MouseEvent} event
   */
  toggle(event) {
    event.preventDefault();
    if (this.#isHome || !this.#desktop.matches) return;
    this.isOpen ? this.close() : this.open();
  }

  open() {
    clearTimeout(this.#settleTimer);
    html.removeAttribute('data-frame-settling');
    html.setAttribute('data-frame-open', '');
  }

  /** Restore the page. The home page never zooms and stays as it is. */
  close() {
    if (!this.isOpen || this.#isHome) return;
    html.removeAttribute('data-frame-open');

    // Suppress the hover preview until the panel is back at 100%, otherwise the
    // pointer still resting on the gutter would catch it mid-transition.
    html.setAttribute('data-frame-settling', '');
    clearTimeout(this.#settleTimer);
    this.#settleTimer = window.setTimeout(this.#settle, this.#panelTransitionMs() + 50);
  }

  #settle = () => {
    clearTimeout(this.#settleTimer);
    html.removeAttribute('data-frame-settling');
    html.removeAttribute('data-frame-zoom-in');
  };

  /** @param {TransitionEvent} event */
  #handleTransitionEnd = (event) => {
    if (event.propertyName !== 'transform' || !(event.target instanceof Element)) return;
    if (!event.target.classList.contains('frame__panel')) return;
    if (!this.isOpen) this.#settle();
  };

  #panelTransitionMs() {
    const value = getComputedStyle(this).getPropertyValue('--panel-transition').trim();
    if (value.endsWith('ms')) return parseFloat(value) || 0;
    if (value.endsWith('s')) return (parseFloat(value) || 0) * 1000;
    return 600;
  }

  /* ------------------------------------------------------------------ */
  /* Mobile menu                                                         */
  /* ------------------------------------------------------------------ */

  get isMenuOpen() {
    return html.hasAttribute('data-menu-open');
  }

  /** The "Index" / "Close" control. */
  toggleMenu() {
    this.isMenuOpen ? this.closeMenu() : this.openMenu();
  }

  /**
   * Slide the menu sheet down. The page behind is pinned at its current
   * scroll offset (body becomes fixed, shifted by --menu-scroll-top) so it
   * cannot scroll under the sheet on touch devices.
   */
  openMenu() {
    if (this.isMenuOpen) return;
    this.#menuScrollY = window.scrollY;
    html.style.setProperty('--menu-scroll-top', `${-this.#menuScrollY}px`);
    html.setAttribute('data-menu-open', '');
    lockScroll(this);
    this.#menuToggle?.setAttribute('aria-expanded', 'true');
  }

  /** Slide the sheet back up and put the page back where it was. */
  closeMenu() {
    if (!this.isMenuOpen) return;
    html.removeAttribute('data-menu-open');
    unlockScroll(this);
    html.style.removeProperty('--menu-scroll-top');
    window.scrollTo({ top: this.#menuScrollY, behavior: 'instant' });
    this.#menuToggle?.setAttribute('aria-expanded', 'false');
  }

  get #menuToggle() {
    return this.querySelector('.frame__menu-toggle');
  }

  /** The menu sheet only exists below 990px; drop it when the viewport crosses up. */
  #handleBreakpoint = () => {
    if (this.#desktop.matches) this.closeMenu();
  };

  /**
   * A plain click on a nav or utility link is about to load another page:
   * drop the current page's dot now rather than when the new page arrives,
   * and, from the menu view, ask the next page to arrive small and grow to
   * full size.
   * @param {MouseEvent} event
   */
  #handleMenuLinkClick = (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target instanceof Element ? event.target.closest('.nav__item a, .utilities__item a') : null;
    if (!link || link.getAttribute('target') === '_blank') return;
    html.setAttribute('data-navigating', '');
    if (!this.isOpen) return;
    try {
      sessionStorage.setItem(FrameComponent.OPEN_ON_LOAD_KEY, 'zoom');
    } catch {
      // Storage unavailable: the next page opens at full size
    }
  };

  /** Back from the bfcache: the page is current again. @param {PageTransitionEvent} event */
  #handlePageShow = (event) => {
    if (event.persisted) html.removeAttribute('data-navigating');
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape') return;
    this.close();
    this.closeMenu();
  };
}

if (!customElements.get('frame-component')) {
  customElements.define('frame-component', FrameComponent);
}
